/**
 * editor-shell.tsx — the shared authoring frame behind BOTH designers
 * (2026-08-31, stage 3 of the editor-unification plan).
 *
 * The email designer's own header says it was "built on the landing editor's
 * shell pattern (palette / canvas / inspector, drag-resizable page width via
 * side handles, undo/redo, autosave)". That pattern was never extracted, so the
 * Journey designer — built later, from a prototype — got none of it: no undo, no
 * redo, no autosave indicator, and a fixed-width canvas. This is the extraction.
 *
 * WHAT THE SHELL OWNS (operator ruling, 2026-08-31):
 *   · the canvas width control: snap presets, free drag from either edge, and a
 *     MEASURED readout (the requested width can be capped by the canvas, and a
 *     readout that lies about it is worse than none)
 *   · the undo/redo stack, generic over the document type, with a `disabled`
 *     mode for when the host is showing something the stack does not cover
 *   · the save-status badge
 *
 * WHAT IT DOES NOT OWN: selection, or the pane frame. Email has one selected
 * block; a journey has a selected block, page, flow step and Setup view at once,
 * so selection stayed with the hosts (operator ruling) — and that is exactly what
 * let both screens adopt this in a single stage. The three-pane frame stayed with
 * them too: each has conditional panes and its own chrome rows above the canvas,
 * and a wrapper component that has to be told about all of that earns nothing.
 * What was actually duplicated, and is now shared, is the behaviour: width, the
 * history stack, the column, and the badge.
 *
 * Width is a PREVIEW AID on both sides and is never persisted: on an email the
 * output column is locked to 600px regardless, and on a page the ruling is that
 * the drag is a check the layout holds, not a property of the document.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";

/* ── Undo / redo ──────────────────────────────────────────────────────── */

export interface EditorHistory<T> {
  /** Call BEFORE mutating, with the current value. */
  push: (current: T) => void;
  undo: (current: T) => T | null;
  redo: (current: T) => T | null;
  canUndo: boolean;
  canRedo: boolean;
  /** Drop both stacks — on load, or when the host switches document. */
  reset: () => void;
  disabled: boolean;
}

const HISTORY_LIMIT = 30;

/**
 * A plain two-stack history. Deliberately NOT a store: the host owns its
 * document and calls `push` before each mutation, so nothing here has to know
 * how a design or a journey is shaped or saved.
 *
 * `disabled` makes every operation a no-op and both flags false — the journey
 * designer passes it while a Setup view is open, because Setup writes to the
 * event or queue RECORD through its own ops and this stack cannot reverse those.
 * An undo button that silently reverses a block edit made ten minutes ago, while
 * the operator is looking at a form, is worse than one that is greyed out.
 */
export function useEditorHistory<T>(opts?: { disabled?: boolean; clone?: (v: T) => T }): EditorHistory<T> {
  const disabled = !!opts?.disabled;
  const clone = opts?.clone ?? ((v: T) => v);
  const [past, setPast] = useState<T[]>([]);
  const [future, setFuture] = useState<T[]>([]);

  const push = useCallback((current: T) => {
    if (disabled) return;
    setPast((p) => [...p.slice(-(HISTORY_LIMIT - 1)), clone(current)]);
    setFuture([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const undo = useCallback((current: T): T | null => {
    if (disabled || !past.length) return null;
    const prev = past[past.length - 1]!;
    setFuture((f) => [clone(current), ...f.slice(0, HISTORY_LIMIT - 1)]);
    setPast((p) => p.slice(0, -1));
    return prev;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, past]);

  const redo = useCallback((current: T): T | null => {
    if (disabled || !future.length) return null;
    const next = future[0]!;
    setPast((p) => [...p.slice(-(HISTORY_LIMIT - 1)), clone(current)]);
    setFuture((f) => f.slice(1));
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, future]);

  const reset = useCallback(() => { setPast([]); setFuture([]); }, []);

  return { push, undo, redo, canUndo: !disabled && past.length > 0, canRedo: !disabled && future.length > 0, reset, disabled };
}

/** Undo / redo pair for a page header. `hint` explains a disabled state rather
 *  than leaving the operator to guess why the buttons are dead. */
export function EditorHistoryButtons<T>({ history, onRestore, current, hint, testidPrefix, size }: {
  history: EditorHistory<T>;
  current: T;
  onRestore: (next: T) => void;
  hint?: string;
  testidPrefix?: string;
  /** Btn tier. Omitted = the default admin control (40); "sm" is the design
   *  system's dense tier (32) for a compact editor toolbar. */
  size?: "sm" | "md";
}) {
  const t = useT();
  const title = history.disabled ? hint : undefined;
  return (
    <>
      <Btn variant="secondary" size={size} disabled={!history.canUndo} title={title}
        onClick={() => { const n = history.undo(current); if (n != null) onRestore(n); }}
        testid={testidPrefix ? `${testidPrefix}-undo` : undefined}>
        {t("editor.shell.undo", "Undo")}
      </Btn>
      <Btn variant="secondary" size={size} disabled={!history.canRedo} title={title}
        onClick={() => { const n = history.redo(current); if (n != null) onRestore(n); }}
        testid={testidPrefix ? `${testidPrefix}-redo` : undefined}>
        {t("editor.shell.redo", "Redo")}
      </Btn>
    </>
  );
}

/* ── Drop-target geometry ─────────────────────────────────────────────── */

/**
 * Which gap in a list a pointer is nearest — the whole list is the target, not
 * the gaps.
 *
 * THE BUG THIS EXISTS TO FIX
 * The first drag-and-drop pass made each gap its own 8px drop target that grew to
 * 18px "once a drag was in flight". For a MOVE that worked, because the dragged
 * block set a flag. For a drag from the PALETTE nothing had set the flag yet, so
 * the lines were still 8px — and the only way to arm them was to already be on
 * one. An 8px moving target, and the operator's word for it was "difficult".
 *
 * So the container takes one `dragover` handler and asks this: given the pointer,
 * which gap is it nearest? Every pixel of the list answers, the indicator is
 * drawn rather than hit, and precision stops mattering.
 *
 * Compares against each child's MIDPOINT, so the answer flips when the pointer
 * passes the middle of a block — the behaviour every list-reorder UI has, and the
 * one an author already expects without being taught.
 */
export function gapIndexFromPointer(container: HTMLElement | null, clientY: number, childSelector: string): number {
  if (!container) return 0;
  const kids = Array.from(container.querySelectorAll<HTMLElement>(childSelector));
  for (let i = 0; i < kids.length; i += 1) {
    const r = kids[i]!.getBoundingClientRect();
    if (clientY < r.top + r.height / 2) return i;
  }
  return kids.length;
}

/* ── Auto-scroll while dragging ───────────────────────────────────────── */

/**
 * Scrolls a container while a drag hovers near its top or bottom edge.
 *
 * Without this a long page is undraggable in one direction: a block at the
 * bottom has nowhere to go, because reaching the top of the list means leaving
 * the viewport and the drag ends.
 *
 * TWO THINGS DECIDE WHETHER THIS WORKS, AND BOTH ARE ABOUT THE LISTENER.
 *
 * It is bound in the CAPTURE phase. Drop targets inside the canvas call
 * `stopPropagation()` on `dragover` — they must, or a line inside a column would
 * also arm the page-level line behind it — so a bubbling listener on the
 * container would never fire while the pointer is over the very targets that
 * need scrolling.
 *
 * And it is bound imperatively rather than as a React `onDragOverCapture` prop,
 * because the scroll must keep running between `dragover` events. The browser
 * fires those only as the pointer MOVES; hold still inside the edge zone and a
 * per-event scroll stops dead. A rAF loop reading the last known position keeps
 * going, which is what makes holding at the edge feel like scrolling rather than
 * like a stall.
 */
export function useDragAutoScroll(opts?: { zone?: number; maxSpeed?: number }): (el: HTMLElement | null) => void {
  const zone = opts?.zone ?? 84;
  const maxSpeed = opts?.maxSpeed ?? 20;
  /* STATE, not a ref, deliberately. The canvas is conditionally rendered, so it
     attaches long after this hook's owner mounted — an effect keyed on a ref
     would have run once against `null` and never bound anything. */
  const [el, setEl] = useState<HTMLElement | null>(null);
  const raf = useRef<number | null>(null);
  const speed = useRef(0);

  useEffect(() => {
    if (!el) return;

    const stop = () => {
      speed.current = 0;
      if (raf.current !== null) { cancelAnimationFrame(raf.current); raf.current = null; }
    };

    const step = () => {
      if (speed.current === 0) { raf.current = null; return; }
      el.scrollTop += speed.current;
      raf.current = requestAnimationFrame(step);
    };

    const onDragOver = (e: DragEvent) => {
      const box = el.getBoundingClientRect();
      const y = e.clientY;
      /* Proximity, 0 at the zone's inner edge and 1 at the container's edge, so
         the scroll eases in instead of jumping to full speed the moment the
         pointer crosses a line. */
      const near = (d: number) => Math.min(1, Math.max(0, (zone - d) / zone));
      const up = near(y - box.top);
      const down = near(box.bottom - y);
      const atTop = el.scrollTop <= 0;
      const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      const next = up > 0 && !atTop ? -Math.ceil(up * maxSpeed)
        : down > 0 && !atEnd ? Math.ceil(down * maxSpeed)
        : 0;
      speed.current = next;
      if (next !== 0 && raf.current === null) raf.current = requestAnimationFrame(step);
      if (next === 0) stop();
    };

    el.addEventListener("dragover", onDragOver, true);
    /* `drop` and `dragend` both matter: a drop onto a target ends the drag
       without a dragend on this element, and an abandoned drag ends without a
       drop. Missing either leaves the container scrolling on its own. */
    window.addEventListener("drop", stop, true);
    window.addEventListener("dragend", stop, true);
    return () => {
      el.removeEventListener("dragover", onDragOver, true);
      window.removeEventListener("drop", stop, true);
      window.removeEventListener("dragend", stop, true);
      stop();
    };
  }, [el, zone, maxSpeed]);

  return setEl;
}

/* ── Canvas width ─────────────────────────────────────────────────────── */

export interface WidthPreset { w: number; label: string }
export interface CanvasWidth {
  /** Requested width, in px. */
  width: number;
  /** Width actually RENDERED — the column carries maxWidth:100%, so a preset
   *  wider than the canvas paints narrower than it asks for. */
  measured: number;
  capped: boolean;
  dragging: boolean;
  setWidth: (w: number) => void;
  /** Attach to the column being measured. */
  ref: (el: HTMLElement | null) => void;
  /** Spread onto a left / right drag handle. */
  handleProps: (side: -1 | 1) => { onPointerDown: (e: { clientX: number; preventDefault: () => void }) => void };
}

export function useCanvasWidth(opts: { initial: number; min?: number; max?: number }): CanvasWidth {
  const min = opts.min ?? 300;
  const max = opts.max ?? 1400;
  const [width, setWidth] = useState(opts.initial);
  const [measured, setMeasured] = useState(opts.initial);
  const [dragging, setDragging] = useState(false);
  const elRef = useRef<HTMLElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  /* Read the rendered width from wherever we are, whenever asked. Kept as a ref
     so every path below — observer, frame, layout effect — calls the same one. */
  const readRef = useRef<() => void>(() => {});
  readRef.current = () => {
    const el = elRef.current;
    if (!el) return;
    const w = Math.round(el.getBoundingClientRect().width);
    if (w) setMeasured((prev) => (prev === w ? prev : w));
  };

  /* The host may change the initial width (a device toggle, a new document). */
  useEffect(() => { setWidth(opts.initial); }, [opts.initial]);

  const ref = useCallback((el: HTMLElement | null) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    elRef.current = el;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => readRef.current());
    ro.observe(el);
    roRef.current = ro;
    readRef.current();
  }, []);

  /* THREE chances to measure, because any one of them can legitimately miss.

     ResizeObserver's first callback can arrive before layout, when the rect is
     still 0 — the `if (w)` guard skips it. And because the RENDERED width then
     never changes (a column asking for 4000px inside a 300px pane is capped at
     300 whatever it asks for), no second callback ever comes: `measured` used to
     stay stuck at `initial` for the lifetime of the editor, so the readout said
     4000px beside a 300px column and `capped` was never true when it should
     always have been.

     A single requestAnimationFrame was the first fix and is not enough on its
     own: rAF does not fire in a hidden tab or a background iframe, so one missed
     frame left the same permanent staleness with no retry.

     So the layout effect carries NO dependency array: it runs after every
     commit, which is the only signal available when ResizeObserver is asleep and
     rAF never comes. The pane can change width without the requested width
     changing at all — an editor panel opening or minimizing does exactly that —
     and keying on `width` missed it. It cannot loop: `readRef.current` only
     setStates when the rounded value actually differs. */
  useLayoutEffect(() => { readRef.current(); });
  useEffect(() => {
    readRef.current();
    const raf = requestAnimationFrame(() => readRef.current());
    /* The pane itself can change width without the column's own box changing —
       a panel opening, the window resizing — and while RO on the column catches
       most of that, a capped column's rect can stay put while `capped` should
       flip. Cheap enough to re-read on window resize too. */
    const onResize = () => readRef.current();
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, [width]);
  useEffect(() => () => { if (roRef.current) roRef.current.disconnect(); }, []);

  const handleProps = useCallback((side: -1 | 1) => ({
    onPointerDown: (e: { clientX: number; preventDefault: () => void }) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = elRef.current?.getBoundingClientRect().width ?? width;
      setDragging(true);
      /* ×2 because the column is centred: the edge moves half as far as the
         width changes. */
      const move = (ev: PointerEvent) => setWidth(Math.round(Math.min(max, Math.max(min, startW + (ev.clientX - startX) * 2 * side))));
      const up = () => {
        setDragging(false);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
  }), [width, min, max]);

  return { width, measured, capped: measured < width - 2, dragging, setWidth, ref, handleProps };
}

const chip = (on: boolean): CSSProperties => ({
  padding: "4px 10px", borderRadius: 999, cursor: "pointer", fontFamily: T.font,
  fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
  border: `1px solid ${on ? "var(--jrni-color-primary-text)" : T.n4}`,
  background: on ? "var(--jrni-color-primary-text)" : T.white,
  color: on ? T.onColor : T.n2,
});

/** Preset row + measured readout. The readout holds the NUMBER only; the
 *  qualifiers sit under the canvas, where there is room for them. */
export function CanvasWidthBar({ width, presets, extra }: {
  width: CanvasWidth;
  presets: WidthPreset[];
  extra?: ReactNode;
}) {
  const t = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.05, textTransform: "uppercase", color: T.soft, whiteSpace: "nowrap" }}>
        {t("editor.shell.width", "Width")}
      </span>
      {presets.map((p) => (
        // ui-raw: canvas width preset — a compact filter chip, no jrni-ui family fits
        <button key={p.w} onClick={() => width.setWidth(p.w)} style={chip(width.width === p.w)}>{p.label}</button>
      ))}
      {extra}
      <span style={{ flex: 1 }} />
      <span style={{ fontFamily: T.fontMono, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0, color: width.dragging ? "var(--jrni-color-primary-text)" : T.soft, fontWeight: width.dragging ? 700 : 400 }}>
        {width.measured}{t("shared.editorShell.text", "px")}</span>
    </div>
  );
}

/** A drag handle. Two of these flank the canvas column. */
export function CanvasEdge({ width, side }: { width: CanvasWidth; side: -1 | 1 }) {
  const t = useT();
  return (
    <div {...width.handleProps(side)} title={t("editor.shell.dragToResize", "Drag to resize")}
      style={{ width: 16, cursor: "ew-resize", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
      <span style={{ width: 4, height: 46, borderRadius: 999, background: T.n4 }} />
    </div>
  );
}

/** The column the canvas content sits in — carries the requested width and the
 *  measuring ref, and animates unless a drag is in progress. */
export function CanvasColumn({ width, children, style }: { width: CanvasWidth; children: ReactNode; style?: CSSProperties }) {
  return (
    <div ref={width.ref as (el: HTMLDivElement | null) => void}
      style={{ width: width.width, maxWidth: "100%", transition: width.dragging ? "none" : "width 120ms ease", ...style }}>
      {children}
    </div>
  );
}

/** Save status, for hosts that write on every edit rather than on a debounce.
 *  `saved` is the resting state — an editor that saves continuously should say
 *  so, not stay silent until something goes wrong. */
export type ShellSaveState = "saved" | "saving" | "error";
export function SavedBadge({ state, label }: { state: ShellSaveState; label?: string }) {
  const t = useT();
  const tone = state === "error" ? T.red1 : state === "saving" ? T.soft : T.green1;
  const text = label ?? (state === "error"
    ? t("editor.shell.saveFailed", "Couldn't save")
    : state === "saving" ? t("editor.shell.saving", "Saving…") : t("editor.shell.saved", "Saved"));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: tone, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: tone }} />
      {text}
    </span>
  );
}
